import React, { Suspense, ComponentProps, ReactNode } from 'react';
import { useLocation } from 'react-router';
import { Titled } from 'react-titled';
import {
  Loading,
  LoadingContentHeader,
  LoadingContentBody,
} from '@asap-hub/react-components';

import ErrorBoundary from './ErrorBoundary';

type FrameProps = {
  title: string | null; // explicit null, omitting prop not allowed to make sure title is not forgotten when adding a page
  children: ReactNode;
  fallback: ComponentProps<typeof Suspense>['fallback'];
};

type FrameBoundaryProps = {
  title: string | null; // explicit null, omitting prop not allowed to make sure title is not forgotten when adding a page
  children?: ReactNode;
  boundaryProps?: Omit<ComponentProps<typeof ErrorBoundary>, 'children'>;
  fallback?: ComponentProps<typeof Suspense>['fallback'];
};

const Frame = ({ fallback, children, title }: FrameProps) => (
  <Titled
    title={(parentTitle: string) =>
      title ? (parentTitle ? `${title} | ${parentTitle}` : title) : parentTitle
    }
  >
    <Suspense fallback={fallback}>{children}</Suspense>
  </Titled>
);

const DefaultFrame: React.FC<FrameBoundaryProps> = ({
  children,
  title,
  boundaryProps,
  fallback = <Loading />,
}) => (
  <ErrorBoundary {...boundaryProps}>
    <Frame title={title} fallback={fallback}>
      {children}
    </Frame>
  </ErrorBoundary>
);

export const SearchFrame: React.FC<
  Omit<FrameBoundaryProps, 'boundaryProps'>
> = ({ children, title, fallback = <Loading /> }) => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.delete('searchQuery');
  const frameKey = params.toString();
  return (
    <ErrorBoundary
      title={'Something went wrong'}
      description={'There was a problem with your search, please try again.'}
      error={new Error()}
    >
      <Frame key={frameKey} title={title} fallback={<LoadingContentBody />}>
        {children}
      </Frame>
    </ErrorBoundary>
  );
};

export const SkeletonHeaderFrame: React.FC<
  Omit<FrameBoundaryProps, 'fallback'>
> = (props) => <DefaultFrame {...props} fallback={<LoadingContentHeader />} />;

export const SkeletonBodyFrame: React.FC<
  Omit<FrameBoundaryProps, 'fallback'>
> = (props) => <DefaultFrame {...props} fallback={<LoadingContentBody />} />;

export default DefaultFrame;
